import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Form, Toast, ListGroup, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard } from '@fortawesome/free-solid-svg-icons';
import * as Sentry from '@sentry/node';
import { gray300 } from '../../ui/colors';
import ToastWrapper from '../ToastWrapper';
import { usePaymentContext } from '../../context/Payment';
import LocalAPI from '../../SDKs/LocalAPI';
import PulseLoader from 'react-spinners/PulseLoader';

// Add CollectJS type declarations
declare global {
  interface Window {
    CollectJS: {
      configure: (config: CollectJSConfig) => void;
      startPaymentRequest: () => void;
    };
  }
}

interface CollectJSConfig {
  variant: string;
  callback: (response: { token: string }) => void;
  validationCallback: (field: string, valid: boolean, message: string) => void;
  fields: {
    [key: string]: {
      placeholder: string;
      selector: string;
    };
  };
  styleSniffer: boolean;
  customCss: {
    [key: string]: string;
  };
}

interface BookingPaymentPropsGratuity {
  tip?: any;
  franchisee?: any;
  customerData?: any;
  submittedTipAmount?: any;
  submitCreditCard?: any;
  collectJsToken: string;
}

const BookingPaymentGratuity: React.FC<BookingPaymentPropsGratuity> = ({
  franchisee,
  customerData,
  submittedTipAmount,
  submitCreditCard,
  collectJsToken,
}) => {
  const [disabled, setDisabled] = useState<boolean>(false);
  const [customerVaultId, setCustomerVaultId] = useState<string>('');
  const { sendToPaymentState } = usePaymentContext();

  const [toastState, setToastState] = useState({
    success: { show: false, message: '' },
    info: { show: false, message: '' },
    error: { show: false, message: '' },
    booked: { show: false, message: '', counter: null },
  });

  const [cardValidation, setCardValidation] = useState({
    card: false,
    exp: false,
    cvv: false,
  });

  const [paymentState, setPaymentState] = useState({
    loading: true,
    buttonDisabled: false,
    collectJSLoaded: false,
  });

  const cardFieldsAreValid = useCallback(
    () => Object.values(cardValidation).every(Boolean),
    [cardValidation]
  );

  const resetCardValidation = () => setCardValidation({ card: false, exp: false, cvv: false });

  const showToast = (type: 'success' | 'info' | 'error' | 'booked', message: string) => {
    setToastState(prev => ({
      ...prev,
      [type]: { show: true, message, counter: type === 'booked' ? 5 : null },
    }));
  };

  const hideToast = (type: 'success' | 'info' | 'error' | 'booked') => {
    setToastState(prev => ({ ...prev, [type]: { ...prev[type], show: false } }));
  };

  useEffect(() => {
    if (toastState.booked.counter > 0) {
      const timer = setInterval(() => 
        setToastState(prev => ({
          ...prev,
          booked: { ...prev.booked, counter: prev.booked.counter - 1 },
        })), 1000);
      return () => clearInterval(timer);
    }
  }, [toastState.booked.counter]);

  const collectJSInitialized = useRef(false);

  useEffect(() => {
    if (!collectJSInitialized.current) {
      const script = document.createElement('script');
      script.src = 'https://secure.paylinedatagateway.com/token/Collect.js';
      script.async = true;
      script.setAttribute('data-tokenization-key', collectJsToken);
      script.onload = () => {
        setPaymentState(prev => ({ ...prev, collectJSLoaded: true }));
        configureCollectJS();
        collectJSInitialized.current = true;
      };
      document.body.appendChild(script);
      return () => {
        document.body.removeChild(script);
      };
    }
  }, [collectJsToken]);

  const configureCollectJS = () => {
    if (typeof window.CollectJS !== 'undefined') {
      window.CollectJS.configure({
        variant: 'inline',
        callback: handleCollectJSResponse,
        validationCallback: handleValidationCallback,
        fields: {
          ccnumber: {
            placeholder: '0000 0000 0000 0000',
            selector: '#ccnumber',
          },
          ccexp: {
            placeholder: 'MM/YY',
            selector: '#ccexp',
          },
          cvv: {
            placeholder: '123',
            selector: '#cvv',
          },
        },
        styleSniffer: false,
        customCss: {
          'border-color': 'rgb(206, 212, 218)',
          'padding': '0.375rem 0.75rem',
          'border': '2px solid rgb(206, 212, 218)',
          'border-style': 'solid',
          'border-radius': '0.25rem',
          'font-size': '1rem',
          'height': '40px',
        },
      });
      setPaymentState(prev => ({ ...prev, loading: false, buttonDisabled: false }));
      resetCardValidation();
      collectJSInitialized.current = true;
    }
  };

  const handleValidationCallback = (field: string, valid: boolean, message: string) => {
    console.log('handleValidationCallback');
    
    setCardValidation(prev => ({ ...prev, [field]: valid }));
    if (!valid) {
      showToast('error', message);
    } else {
      hideToast('error');
      setDisabled(false);
    }
    
    if (cardFieldsAreValid()) {
      sendToPaymentState('CARD_VALID');
      sendToPaymentState('READY');
    } else {
      sendToPaymentState('CARD_INVALID');
    }
  };

  const handleButtonClick = () => {
    setPaymentState(prev => ({ ...prev, loading: true, buttonDisabled: true }));
    window.CollectJS.startPaymentRequest();
  };

  const handleCollectJSResponse = async (response: { token: string }) => {
    console.log('handleCollectJSResponse')
    setPaymentState(prev => ({ ...prev, buttonDisabled: true }));
    const paylineToken: string = response.token;
    const setPaymentMethodData = { ...customerData, paylineToken, price: submittedTipAmount };

    try {
      const result = await LocalAPI.setPaymentMethod(setPaymentMethodData);
      if (result.status === 201) {
        const vaultId: string = result?.data.data.customer_vault_id;
        setCustomerVaultId(vaultId);
        sendToPaymentState('RESOLVE');
        showToast('info', 'Card Info Set');
        submitCreditCard(vaultId);
      } else if (customerVaultId !== '') {
        sendToPaymentState('REJECT');
        sendToPaymentState('DUPLICATE');
      } else {
        showToast('error', 'Your Card was Declined');
        sendToPaymentState('REJECT');
        sendToPaymentState('DECLINED');
      }
    } catch (error) {
      Sentry.captureException(error);
      sendToPaymentState('REJECT');
      sendToPaymentState('ERROR');
    } finally {
      setPaymentState(prev => ({ ...prev, loading: false, buttonDisabled: false }));
    }
  };

  return (
    <>
      <Form
        id='booking-form-payment'
        className={`needs-validation book__gated ${disabled ? '' : 'active'}`}
        noValidate
      >
        <div className='font-weight-bold mt-5 mb-4 text-left'>
          Payment Information
        </div>
        <Form.Row className='align-items-start'>
          <Form.Group className='col-12 col-md-6'>
            <Form.Label htmlFor='paymentInfoCC' className='d-flex justify-content-between'>
              Credit Card
              <FontAwesomeIcon icon={faCreditCard} className='mr-2' size='lg' color={gray300} />
            </Form.Label>
            <div id='ccnumber' style={{ height: 'calc(1.5em + .75rem + 4px)' }} />
          </Form.Group>
          <Form.Group className='col-6 col-md-3'>
            <Form.Label style={{ width: '100%', textAlign: 'initial' }} htmlFor='paymentInfoExpiry'>
              Expiration Date
            </Form.Label>
            <div id='ccexp' style={{ height: 'calc(1.5em + .75rem + 4px)' }} />
          </Form.Group>
          <Form.Group className='col-6 col-md-3'>
            <Form.Label htmlFor='paymentInfoCVC' style={{ width: '100%', textAlign: 'initial' }}>
              CVV
            </Form.Label>
            <div id='cvv' style={{ height: 'calc(1.5em + .75rem + 4px)' }} />
          </Form.Group>
        </Form.Row>
      </Form>
      <div className='cc-provider-badge-container'>
        <img src="https://res.cloudinary.com/twomaidsengi/image/upload/v1742589672/Visa_dqtlkb.webp" alt="Visa" />
        <img src="https://res.cloudinary.com/twomaidsengi/image/upload/v1742589672/mastercard_u4wd9k.webp" alt="Mastercard" />
        <img src="https://res.cloudinary.com/twomaidsengi/image/upload/v1742589672/Discover_x78dvz.webp" alt="Discover" />
        <img src="https://res.cloudinary.com/twomaidsengi/image/upload/v1742589672/AmericanExpress_a8rqed.webp" alt="American Express" />
      </div>
      <Button
        disabled={paymentState.buttonDisabled || !paymentState.collectJSLoaded}
        onClick={handleButtonClick}
      >
        {paymentState.loading ? <PulseLoader color='#fff' size={8} /> : 'Submit'}
      </Button>
      <ToastWrapper>
        {Object.entries(toastState).map(([key, value]) => (
          <Toast
            key={key}
            show={value.show}
            onClose={() => hideToast(key as any)}
            autohide
            delay={8000}
          >
            <Toast.Body>
              <ListGroup>
                <ListGroup.Item variant={key === 'error' ? 'danger' : 'success'}>
                  {value.message}
                  {key === 'booked' && <span className='d-block'>Redirecting in: {value}</span>}
                </ListGroup.Item>
              </ListGroup>
            </Toast.Body>
          </Toast>
        ))}
      </ToastWrapper>
    </>
  );
};

export default BookingPaymentGratuity;